import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

const forecastDays = Array.from({ length: 21 }, (_, index) => index);
const safetyStock = 25;
const initialStock = 106;
const shipmentUnits = 210;
const baseDailyDemand = 18;
const averageGrossProfitPerUnit = 24;

const getDayLabel = (day) => `D+${day}`;

const getProfitAxisMax = (maxValue) => {
  if (maxValue <= 0) {
    return 200;
  }

  const paddedValue = maxValue * 1.15;
  const step = paddedValue <= 1200 ? 200 : 500;

  return Math.ceil(paddedValue / step) * step;
};

const xAxisTicks = {
  compact: [0, 4, 8, 12, 16, 20].map(getDayLabel),
  regular: [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20].map(getDayLabel)
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const stock = payload.find((item) => item.dataKey === 'stock')?.value;
  const profitAtRisk = payload.find((item) => item.dataKey === 'profitAtRisk')?.value;

  return (
    <div className="rounded-sm bg-slate-900/90 px-4 py-3 text-left shadow-xl">
      <p className="text-lg font-bold text-white">Stock Level (Units) ↑ {stock}</p>
      <p className="text-lg font-bold text-emerald-300">Gross Profit at Risk ${profitAtRisk?.toLocaleString()}</p>
      <p className="text-lg font-bold text-slate-300">Days Forecast → {label.replace('D+', '')}</p>
    </div>
  );
};

const SafetyStockLabel = ({ viewBox, isCompact }) => {
  if (!viewBox) {
    return null;
  }

  const labelWidth = isCompact ? 78 : 92;
  const labelHeight = isCompact ? 20 : 24;
  const x = (viewBox.x ?? 0) + 54;
  const y = (viewBox.y ?? 0) - labelHeight - 7;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={labelWidth}
        height={labelHeight}
        rx="4"
        fill="#0e0e0f"
        fillOpacity="0.92"
        stroke="#475569"
        strokeOpacity="0.75"
      />
      <text
        x={x + labelWidth / 2}
        y={y + labelHeight / 2 + 4}
        fill="#f8fafc"
        fontSize={isCompact ? 10 : 12}
        fontWeight="700"
        textAnchor="middle"
      >
        Safety Stock
      </text>
    </g>
  );
};

const InventoryMarginDashboard = () => {
  const [demandForecast, setDemandForecast] = useState(1.2);
  const [leadTime, setLeadTime] = useState(7);
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 640px)');
    const syncViewport = () => setIsCompact(mediaQuery.matches);

    syncViewport();
    mediaQuery.addEventListener('change', syncViewport);

    return () => mediaQuery.removeEventListener('change', syncViewport);
  }, []);

  const data = useMemo(() => {
    const dailyDemand = baseDailyDemand * demandForecast;

    return forecastDays.map((day) => {
      const shipmentReceived = day >= leadTime ? shipmentUnits : 0;
      const stock = Math.max(0, Math.round(initialStock + shipmentReceived - dailyDemand * day));
      const shortageUnits = Math.max(0, Math.round(dailyDemand * day - initialStock - shipmentReceived));

      return {
        day: getDayLabel(day),
        stock,
        profitAtRisk: shortageUnits * averageGrossProfitPerUnit
      };
    });
  }, [demandForecast, leadTime]);

  const arrivalLabel = getDayLabel(leadTime);
  const arrivalPoint = data.find((point) => point.day === arrivalLabel);
  const stockAtArrival = arrivalPoint?.stock ?? 0;
  const stockoutPoint = data.find((point) => {
    const day = Number.parseInt(point.day.replace('D+', ''), 10);
    return day < leadTime && point.stock <= safetyStock;
  });
  const hasStockoutRisk = Boolean(stockoutPoint);
  const riskStatus = hasStockoutRisk ? 'Critical' : 'Controlled';
  const estimatedRoi = Math.round(15999 * demandForecast + Math.max(0, 7 - leadTime) * 850);
  const calloutLeft = `${Math.min(72, Math.max(18, (leadTime / 20) * 100 - 8))}%`;
  const visibleXAxisTicks = isCompact ? xAxisTicks.compact : xAxisTicks.regular;
  const profitAxisMax = getProfitAxisMax(Math.max(...data.map((point) => point.profitAtRisk)));
  const profitAxisTicks = [0, profitAxisMax / 2, profitAxisMax];

  return (
    <div className="w-full overflow-hidden rounded-lg bg-[#0e0e0f] px-4 py-5 text-white shadow-2xl shadow-black/40 md:px-8 md:py-8">
      <div className="relative h-[330px] md:h-[420px]">
        <div className="absolute left-14 top-0 z-10 text-sm font-semibold text-neutral-100 md:left-20 md:text-base">
          Stock Level (Units) ↑
        </div>
        <div className="absolute right-5 top-0 z-10 text-sm font-semibold text-[#82e58c] md:right-12 md:text-base">
          Gross Profit at Risk ($) ↑
        </div>
        <div className="absolute right-1 bottom-1 z-10 hidden text-sm font-semibold text-neutral-100 md:block">
          Days Forecast →
        </div>

        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 42, right: 24, bottom: 40, left: 48 }}>
            <CartesianGrid vertical={false} stroke="#5c5c5c" strokeOpacity={0.8} strokeWidth={1} />
            <XAxis
              dataKey="day"
              axisLine={{ stroke: '#9ca3af' }}
              tickLine={false}
              ticks={visibleXAxisTicks}
              interval={0}
              minTickGap={8}
              tick={{ fill: '#f4f4f5', fontSize: isCompact ? 11 : 13, fontWeight: 600 }}
              tickMargin={10}
              tickFormatter={(value) => value.replace('D+', 'D')}
            />
            <YAxis
              yAxisId="stock"
              domain={[0, 300]}
              ticks={[0, 50, 100, 150, 200, 250, 300]}
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#f4f4f5', fontSize: isCompact ? 12 : 14, fontWeight: 600 }}
              width={46}
            />
            <YAxis
              yAxisId="profitAtRisk"
              orientation="right"
              domain={[0, profitAxisMax]}
              ticks={profitAxisTicks}
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#82e58c', fontSize: isCompact ? 11 : 13, fontWeight: 600 }}
              tickFormatter={(value) => value >= 1000 ? `$${value / 1000}k` : `$${value}`}
              width={34}
            />
            <Tooltip content={<CustomTooltip />} cursor={false} />
            {hasStockoutRisk && (
              <ReferenceArea
                yAxisId="stock"
                x1={stockoutPoint.day}
                x2={arrivalLabel}
                y1={0}
                y2={safetyStock}
                fill="#fb7185"
                fillOpacity={0.38}
                stroke="#fb7185"
                strokeOpacity={0.9}
              />
            )}
            <ReferenceLine
              yAxisId="stock"
              x={arrivalLabel}
              stroke="#f8fafc"
              strokeWidth={1.5}
              label={{ value: 'Shipment Arrival', fill: '#f8fafc', fontSize: isCompact ? 10 : 12, fontWeight: 700, position: 'insideTop', dy: -16 }}
            />
            <Bar yAxisId="stock" dataKey="stock" fill="#70a8fb" barSize={26} radius={[4, 4, 4, 4]} name="Stock Level" />
            <Line
              yAxisId="profitAtRisk"
              type="monotone"
              dataKey="profitAtRisk"
              stroke="#40c96f"
              strokeWidth={4}
              dot={{ r: 4, strokeWidth: 0, fill: '#40d978' }}
              activeDot={{ r: 5, fill: '#40d978', stroke: '#ffffff', strokeWidth: 1 }}
              name="Gross Profit at Risk"
            />
            <ReferenceLine
              yAxisId="stock"
              y={safetyStock}
              stroke="#d1d5db"
              strokeDasharray="5 7"
              strokeWidth={1.5}
              label={{ content: (props) => <SafetyStockLabel {...props} isCompact={isCompact} /> }}
            />
          </ComposedChart>
        </ResponsiveContainer>

        <div
          className="pointer-events-none absolute top-[40%] hidden bg-slate-900/85 px-4 py-3 text-left shadow-xl md:block"
          style={{ left: calloutLeft }}
        >
          <p className="text-base font-bold text-white">Stock Level (Units) ↑ {stockAtArrival}</p>
          <p className="text-base font-bold text-neutral-300">Days Forecast → {leadTime}</p>
        </div>
      </div>

      <div className="-mt-4 flex w-fit flex-wrap items-center gap-4 rounded-full bg-[#151516] px-5 py-3 text-sm text-neutral-200 md:-mt-5 md:text-base">
        <span className="inline-flex items-center gap-2"><span className="h-4 w-3 rounded-sm bg-[#70a8fb]" />Stock Level</span>
        <span className="inline-flex items-center gap-2"><span className="h-1 w-6 rounded-full bg-[#40c96f]" />Gross Profit at Risk</span>
        <span className="inline-flex items-center gap-2"><span className="h-3 w-5 rounded-sm border border-[#fb7185] bg-[#fb7185]/40" />Stockout Risk</span>
      </div>

      <div className="mx-auto mt-8 grid max-w-3xl grid-cols-[1fr_auto_1fr] items-center text-center md:mt-10">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-neutral-300 md:text-sm">Estimated ROI</p>
          <p className="mt-1 text-xl font-bold text-white md:text-2xl">${estimatedRoi.toLocaleString()}</p>
        </div>
        <div className="h-8 w-px bg-neutral-300/80 md:h-10" />
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-neutral-300 md:text-sm">Risk Status</p>
          <p className="mt-1 text-xl font-bold text-white md:text-2xl">{riskStatus}</p>
        </div>
      </div>

      <div className="mt-8 space-y-5 md:mt-10 md:space-y-6">
        <div>
          <div className="mb-3 flex items-center justify-between gap-4">
            <label htmlFor="demandForecast" className="text-base font-medium text-white md:text-lg">AI Demand Forecast</label>
            <div className="min-w-14 rounded-lg bg-[#171718] px-4 py-2 text-center text-lg font-semibold text-white md:min-w-16">
              {demandForecast.toFixed(1).replace('.0', '')}
            </div>
          </div>
          <input
            id="demandForecast"
            type="range"
            min="0.5"
            max="2"
            step="0.1"
            value={demandForecast}
            onChange={(event) => setDemandForecast(Number.parseFloat(event.target.value))}
            className="h-1 w-full accent-white"
          />
        </div>

        <div>
          <div className="mb-3 flex items-center justify-between gap-4">
            <label htmlFor="leadTime" className="text-base font-medium text-white md:text-lg">Supplier Lead Time</label>
            <div className="min-w-14 rounded-lg bg-[#171718] px-4 py-2 text-center text-lg font-semibold text-white md:min-w-16">
              {leadTime}
            </div>
          </div>
          <input
            id="leadTime"
            type="range"
            min="1"
            max="14"
            step="1"
            value={leadTime}
            onChange={(event) => setLeadTime(Number.parseInt(event.target.value, 10))}
            className="h-1 w-full accent-white"
          />
        </div>
      </div>
    </div>
  );
};

export default InventoryMarginDashboard;
